import { CtaBand } from "@/components/site/CtaBand";
import { SiteLayout } from "@/components/site/SiteLayout";
import {
  ArrowRight,
  Award,
  Check,
  Clock3,
  HeartPulse,
  MapPin,
  MoveRight,
  Phone,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { Link } from "wouter";
import { SITE_DETAILS } from "@shared/site";

const careSteps = [
  {
    number: "01",
    title: "Understand what is happening",
    body: "Your doctor of physical therapy listens, evaluates how you move, and looks beyond the most obvious symptom.",
  },
  {
    number: "02",
    title: "Build a plan around you",
    body: "Together, we create a practical path based on your goals, your life, and the activities that matter most.",
  },
  {
    number: "03",
    title: "Keep moving forward",
    body: "We help you build the capacity and confidence to return to meaningful activity with useful strategies for the road ahead.",
  },
];

const outcomes = [
  "Return to the activities you miss",
  "Understand your body",
  "Build strength for everyday life",
  "Move with more confidence",
];

const CLINIC_MAP_IMAGE = "/manus-storage/core-clinic-street-map_1067ed55.webp";

export default function Home() {
  return (
    <SiteLayout>
        <section className="hero-section">
          <div className="hero-orbit hero-orbit-one" aria-hidden="true" />
          <div className="hero-orbit hero-orbit-two" aria-hidden="true" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow"><span /> One-on-one physical therapy in Overland Park, KS</p>
              <h1>Move better. Stay active. <em>Live on your terms.</em></h1>
              <p className="hero-lede">
                When pain or limited movement starts deciding what you can do, we help you understand the problem and build a thoughtful path forward.
              </p>
              <div className="hero-actions">
                <Link className="button-primary" href="/inquiry">
                  Book Consultation <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <a className="button-secondary" href={SITE_DETAILS.phoneHref}>
                  <Phone size={17} aria-hidden="true" /> {SITE_DETAILS.phoneDisplay}
                </a>
              </div>
              <div className="hero-trust" aria-label="Practice highlights">
                <span><ShieldCheck size={18} aria-hidden="true" /> {SITE_DETAILS.legacyStatement}</span>
                <span><UsersRound size={18} aria-hidden="true" /> Individual attention</span>
              </div>
            </div>

            <div className="hero-visual">
              <div className="hero-image-frame">
                <img
                  src="/manus-storage/core-pt-provider-50plus-hero_8d309f52.webp"
                  alt="A physical therapist guiding shoulder movement with an active woman over 50 during a one-on-one session"
                  width="1600"
                  height="1180"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <div className="hero-note">
                <span className="hero-note-icon"><Sparkles size={18} aria-hidden="true" /></span>
                <div>
                  <strong>Your goals shape the plan.</strong>
                  <span>Care designed around real life.</span>
                </div>
              </div>
              <div className="hero-caption">Movement is personal. Your care should be, too.</div>
            </div>
          </div>
        </section>

        <section className="intro-band" aria-labelledby="intro-title">
          <div className="container intro-grid">
            <div>
              <p className="section-kicker">A different kind of physical therapy</p>
              <h2 id="intro-title">You deserve more than a quick fix.</h2>
            </div>
            <div>
              <p>
                Our work starts with the whole person—not a body part in isolation. We combine careful evaluation, one-on-one care, movement expertise, and clear education so you can make informed decisions about what comes next.
              </p>
              <Link className="text-link" href="/services">Explore our approach <MoveRight size={17} aria-hidden="true" /></Link>
            </div>
          </div>
          <div className="container outcomes-row">
            {outcomes.map((outcome) => (
              <span key={outcome}><Check size={17} aria-hidden="true" /> {outcome}</span>
            ))}
          </div>
        </section>

        <section className="care-section" id="care" aria-labelledby="care-title">
          <div className="container">
            <div className="section-heading centered">
              <p className="section-kicker">A clear path forward</p>
              <h2 id="care-title">Your care, in three thoughtful steps.</h2>
              <p>We listen first, build the plan together, and keep your real-world goals at the center.</p>
            </div>
            <div className="care-grid">
              {careSteps.map((step, index) => (
                <article className="care-card" key={step.number}>
                  <div className="care-number">{step.number}</div>
                  <div className="care-line" aria-hidden="true"><span /></div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  {index === 0 && <span className="care-tag">Start here</span>}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="vitality-feature" id="vitality" aria-labelledby="vitality-title">
          <div className="vitality-glow" aria-hidden="true" />
          <div className="container vitality-grid">
            <div className="vitality-image-wrap">
              <img
                src="/manus-storage/core-vitality-adventure_329e8016.webp"
                alt="Two active adults over 50 hiking confidently together on a wooded trail"
                width="1600"
                height="1200"
                loading="lazy"
              />
            </div>
            <div className="vitality-copy">
              <p className="section-kicker light">Vitality Program · Adults 50+</p>
              <h2 id="vitality-title">How do you want to age?</h2>
              <p className="vitality-lede">
                Maybe it looks like traveling with confidence. Keeping up with the people you love. Getting down to the garden—and back up again. Or simply knowing your body is ready for what the day asks of it.
              </p>
              <div className="vitality-points">
                <span><HeartPulse size={20} /> Strength for real life</span>
                <span><ShieldCheck size={20} /> Confidence through practice</span>
                <span><UsersRound size={20} /> Encouragement and connection</span>
              </div>
              <p>
                Vitality is an encouraging fitness experience for adults 50+ who want to keep building strength, balance, mobility, and options for the years ahead.
              </p>
              <Link className="button-light" href="/vitality-class">
                Discover Vitality <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section className="recognition-section" aria-labelledby="recognition-title">
          <div className="container recognition-grid">
            <div className="recognition-copy">
              <p className="section-kicker">Rooted in this community</p>
              <h2 id="recognition-title">Serving Overland Park since 2007.</h2>
              <p>
                CORE Strategies PT has grown by staying focused on the same idea: listen carefully, think collaboratively, and help people build more confidence in how they move.
              </p>
              <div className="recognition-stats">
                <div><strong>Since 2007</strong><span>Local, independent care</span></div>
                <div><strong>One-on-one</strong><span>Time focused on you</span></div>
              </div>
              <a
                className="text-link"
                href="https://www.google.com/maps/search/CORE+Strategies+Physical+Therapy+10400+W+103rd+St+Overland+Park+KS+66214"
                target="_blank"
                rel="noreferrer"
              >
                Read our Google reviews <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
            <div className="award-panel">
              <div className="award-panel-head">
                <Award size={22} aria-hidden="true" />
                <div><strong>Community Recognition: Medals Across the Years</strong><span>2026 Silver Best Physical Therapy recognition.</span></div>
              </div>
              <figure className="award-winner">
                <img
                  src="/manus-storage/core-2026-silver-best-physical-therapy_b9a140fc.webp"
                  alt="2026 Silver Best Physical Therapy recognition for CORE Strategies Physical Therapy and Movement Centre"
                  width="1185"
                  height="457"
                  loading="lazy"
                />
                <figcaption>2026 Silver Best Physical Therapy</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="location-section" id="visit" aria-labelledby="location-title">
          <div className="container location-card">
            <a
              className="location-visual"
              href={SITE_DETAILS.directionsUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Near W 103rd St & Mastin St — open directions to CORE Strategies PT"
            >
              <img
                className="clinic-map"
                src={CLINIC_MAP_IMAGE}
                alt="Street map showing CORE Strategies PT on West 103rd Street near Mastin Street and US 69"
                width="1400"
                height="980"
                loading="lazy"
              />
              <span className="map-cross-street">Near W 103rd St &amp; Mastin St</span>
            </a>
            <div className="location-content">
              <p className="section-kicker">Visit us in Overland Park</p>
              <h2 id="location-title">Care close to home.</h2>
              <address>
                <MapPin size={20} aria-hidden="true" />
                <span>
                  10400 W 103rd St #22<br />
                  Overland Park, KS 66214
                  <small className="location-cross-street">Near W 103rd St &amp; Mastin St</small>
                </span>
              </address>
              <div className="location-hours">
                <Clock3 size={20} aria-hidden="true" />
                <span>Monday–Friday<br /><strong>7 a.m.–6 p.m.</strong></span>
              </div>
              <div className="location-actions">
                <a className="button-primary" href={SITE_DETAILS.directionsUrl} target="_blank" rel="noreferrer">
                  Get directions <ArrowRight size={18} />
                </a>
                <Link className="text-link" href="/contact">Contact the office <MoveRight size={17} /></Link>
              </div>
            </div>
          </div>
        </section>

        <CtaBand title="Let’s talk about what you want to get back to." eyebrow="Ready when you are" />
    </SiteLayout>
  );
}
